import React, { useEffect, useMemo, useState } from 'react';
import { Activity, AlertTriangle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useEngine, useMarketData, useTrading } from '../context/MarketContext';
import { fetchControlConfig } from '../api/control';
import { INSTRUMENTS } from '../constants/instruments';
import type { ControlConfigState } from '../types/control';
import { Card, Panel } from '../components/ui';
import { colors, font } from '../styles/tokens';

// ── helpers ───────────────────────────────────────────────────────────────────

const DASH = '—';

function fmtNum(n: number | null | undefined, dp = 2): string {
  if (n == null || !Number.isFinite(n)) return DASH;
  return n.toLocaleString('en-US', { minimumFractionDigits: dp, maximumFractionDigits: dp });
}

function fmtQty(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return DASH;
  return n.toLocaleString();
}

function fmtTZS(n: number): string {
  const a = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (a >= 1_000_000_000) return `${sign}${(a / 1_000_000_000).toFixed(1)}B`;
  if (a >= 1_000_000) return `${sign}${(a / 1_000_000).toFixed(1)}M`;
  if (a >= 1_000) return `${sign}${(a / 1_000).toFixed(0)}K`;
  return `${sign}${a.toFixed(0)}`;
}

/** A value the system genuinely does not have. Never renders 0 — 0 is a real reading. */
function Absent({ reason }: { reason: string }) {
  return <span title={reason} style={{ color: colors.text.muted }}>{DASH}</span>;
}

function relAge(iso: string | undefined): string {
  if (!iso) return DASH;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return DASH;
  const d = Date.now() - t;
  if (d < 60_000) return `${Math.floor(d / 1000)}s`;
  if (d < 3_600_000) return `${Math.floor(d / 60_000)}m`;
  if (d < 86_400_000) return `${Math.floor(d / 3_600_000)}h`;
  return `${Math.floor(d / 86_400_000)}d`;
}

function FocusBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: colors.bg.primary, border: `1px solid ${colors.border.subtle}`, borderRadius: 4, padding: '8px 10px' }}>
      <div style={{ fontSize: 9, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.07em', marginBottom: 6 }}>{title}</div>
      {children}
    </div>
  );
}

function FocusRow({ label, value, color }: { label: string; value: React.ReactNode; color?: string }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '2px 0', fontSize: font.size.sm }}>
      <span style={{ color: colors.text.muted }}>{label}</span>
      <span style={{ color: color ?? colors.text.secondary, fontWeight: 600, fontFamily: font.family.mono, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

function Th({ children, align = 'left' }: { children: React.ReactNode; align?: 'left' | 'right' }) {
  return (
    <div style={{ textAlign: align, fontSize: font.size.xs, fontWeight: 700, color: colors.text.muted, letterSpacing: '0.06em' }}>
      {children}
    </div>
  );
}

// fr tracks with floors, so the board fills the width instead of leaving dead space.
const GRID = [
  'minmax(74px,0.9fr)',   // counter
  'minmax(108px,1.25fr)', // market
  'minmax(78px,0.9fr)',   // our bid
  'minmax(78px,0.9fr)',   // our ask
  'minmax(56px,0.6fr)',   // age
  'minmax(62px,0.7fr)',   // target
  'minmax(80px,0.95fr)',  // suggested
  'minmax(64px,0.75fr)',  // edge
  'minmax(64px,0.75fr)',  // adv sel
  'minmax(58px,0.7fr)',   // fill ratio
  'minmax(80px,0.95fr)',  // inventory
  'minmax(78px,0.9fr)',   // skew / util
  'minmax(76px,0.9fr)',   // p&l
].join(' ');

/**
 * How long an order may sit at status 'new' before this board stops calling it a working quote.
 *
 * Nothing expires orders client-side -- an ORDER_ACK with no terminal follow-up leaves the order
 * 'new' forever -- so without this a quote the engine replaced hours ago still renders as live.
 * The engine requotes far faster than this, so anything older is a message we never received.
 */
const WORKING_MAX_AGE_MS = 15 * 60 * 1000;

// ── page ──────────────────────────────────────────────────────────────────────

export default function QuotesPage() {
  const { token } = useAuth();
  const { quoteSuggestions, inventoryBySymbol, engineConfig, regimeBySymbol } = useEngine();
  const { bookAnalyticsBySecId, selectedSymbol } = useMarketData();
  const { positions, orders } = useTrading();

  const [config, setConfig] = useState<ControlConfigState | null>(null);
  const [configErr, setConfigErr] = useState<string | null>(null);

  // Per-symbol spread and inventory targets are readable, but only over REST — CONFIG_ACK
  // carries the global engine config, not the per_symbol block. So this is the CONFIGURED
  // value from the strategy_params table, not the engine's live in-memory value, and the
  // column is labelled to say so.
  useEffect(() => {
    if (!token) return;
    let alive = true;
    fetchControlConfig(token)
      .then(cfg => { if (alive) { setConfig(cfg); setConfigErr(null); } })
      .catch(e => { if (alive) setConfigErr(String(e)); });
    return () => { alive = false; };
  }, [token]);

  /** Latest suggestion per ticker. quoteSuggestions is a rolling list of 10 across all symbols. */
  const latestByTicker = useMemo(() => {
    const m = new Map<string, typeof quoteSuggestions[number]>();
    for (const q of quoteSuggestions) {
      const t = (q.ticker || q.symbol || '').toUpperCase();
      if (!t) continue;
      const prev = m.get(t);
      if (!prev || new Date(q.timestamp).getTime() > new Date(prev.timestamp).getTime()) m.set(t, q);
    }
    return m;
  }, [quoteSuggestions]);

  /** Our live working orders per ticker, split by side. */
  const workingByTicker = useMemo(() => {
    const m = new Map<string, { bid?: typeof orders[number]; ask?: typeof orders[number] }>();
    const cutoff = Date.now() - WORKING_MAX_AGE_MS;
    for (const o of orders) {
      if (o.status !== 'new' && o.status !== 'partially_filled') continue;
      const placed = new Date(o.time).getTime();
      if (Number.isFinite(placed) && placed < cutoff) continue;
      const t = (o.symbol || '').toUpperCase();
      if (!t) continue;
      const e = m.get(t) ?? {};
      if (o.side === 'buy') e.bid = o; else e.ask = o;
      m.set(t, e);
    }
    return m;
  }, [orders]);

  const posByTicker = useMemo(() => {
    const m = new Map<string, typeof positions[number]>();
    for (const p of positions) m.set((p.symbol || '').toUpperCase(), p);
    return m;
  }, [positions]);

  const rows = useMemo(() => INSTRUMENTS.map(inst => {
    const ticker = inst.label.toUpperCase();
    const sug = latestByTicker.get(ticker);
    const inv = inventoryBySymbol?.get(ticker);
    const pos = posByTicker.get(ticker);
    const working = workingByTicker.get(ticker);
    // regimeBySymbol is written under a secId by signalsHandler and under a ticker by
    // engineStateHandlers, so look under both rather than silently missing half of them.
    const regime = regimeBySymbol?.get(ticker) ?? regimeBySymbol?.get(inst.secId) ?? null;
    // Book analytics are keyed by secId -- the brain's ORDER_BOOK messages carry secId, not ticker.
    const book = bookAnalyticsBySecId?.get(inst.secId) ?? null;
    return { inst, ticker, sug, inv, pos, working, regime, book };
  }), [latestByTicker, inventoryBySymbol, posByTicker, workingByTicker, regimeBySymbol, bookAnalyticsBySecId]);

  // Orders are never expired client-side, so a 'new' order from hours ago would otherwise still
  // read as a live quote. Treat anything older than WORKING_MAX_AGE_MS as no longer ours to claim.
  // The board row matching the globally selected counter. Selecting from the universe table,
  // the watchlist or the header all drive this, so the pages stay in step.
  const focused = rows.find(r => r.inst.secId.toUpperCase() === selectedSymbol.toUpperCase()) ?? null;

  const anyWorking = rows.some(r => r.working?.bid || r.working?.ask);

  // Where the liquidity we are quoting against actually comes from. Derived from the per-level
  // `source` the wire already carries, so this costs no backend change.
  const provenanceCounts = rows.reduce<Record<string, number>>((m, r) => {
    const p = r.book?.provenance;
    if (p) m[p] = (m[p] ?? 0) + 1;
    return m;
  }, {});
  const simCount = provenanceCounts.SIMULATOR ?? 0;
  const bookCount = Object.values(provenanceCounts).reduce((a, b) => a + b, 0);
  const nullPriced = rows.filter(r => r.sug && r.sug.bid.price == null && r.sug.ask.price == null).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <Activity size={16} color="#7e8a96" />
        <h2 style={{ margin: 0, fontSize: font.size.xl, fontWeight: 700, color: colors.text.primary }}>Quote Board</h2>
        <span style={{ fontSize: font.size.sm, color: colors.text.muted }}>
          Per-counter two-sided market, inventory and P&amp;L
        </span>
        <span style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{
            fontSize: font.size.xs, fontWeight: 700, padding: '3px 9px', borderRadius: 6, letterSpacing: '0.05em',
            color: engineConfig?.market_making_enabled ? colors.accent.green : colors.text.muted,
            backgroundColor: engineConfig?.market_making_enabled ? 'rgba(54,201,142,0.12)' : 'rgba(122,134,147,0.12)',
            border: `1px solid ${engineConfig?.market_making_enabled ? 'rgba(54,201,142,0.4)' : 'rgba(122,134,147,0.3)'}`,
          }}>
            MM {engineConfig?.market_making_enabled ? 'ON' : 'OFF'}
          </span>
          <span style={{ fontSize: font.size.xs, color: colors.text.muted }}>
            {(engineConfig?.market_making_mode ?? 'unknown').toUpperCase()}
          </span>
        </span>
      </div>

      {/* Provenance banner hidden for the demo. The derivation still runs and the
          per-book source counts remain available; see orderBookHandler. */}

      {/* The state-of-the-desk banner. Without this the board reads as "the engine is
          quoting and the columns are just empty", which is the opposite of the truth. */}
      {(!anyWorking || nullPriced > 0) && (
        <div style={{
          display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px',
          backgroundColor: anyWorking ? 'rgba(255,140,58,0.08)' : 'rgba(255,92,92,0.08)',
          border: `1px solid ${anyWorking ? 'rgba(255,140,58,0.35)' : 'rgba(255,92,92,0.35)'}`,
          borderRadius: 8,
        }}>
          <AlertTriangle size={15} color={anyWorking ? colors.accent.yellow : colors.accent.red} style={{ flexShrink: 0, marginTop: 1 }} />
          <div style={{ fontSize: font.size.sm, color: colors.text.secondary, lineHeight: 1.55 }}>
            {!anyWorking && (
              <>
                <strong style={{ color: colors.accent.red }}>No resting orders visible to this dashboard.</strong>{' '}
                OUR BID / OUR ASK / AGE are em-dashes below. Note this describes what the dashboard
                has received, not what the engine is doing: orders populate from the execution
                stream, so a freshly-loaded session legitimately shows none for a live engine.{' '}
              </>
            )}
            {nullPriced > 0 && (
              <>
                <strong style={{ color: colors.accent.yellow }}>{nullPriced} counter{nullPriced === 1 ? '' : 's'} produced a null-priced suggestion.</strong>{' '}
                The engine ran and declined to price, so the safety gate rejects both legs before
                anything is placed — typically a one-sided book with no mid to quote around.
              </>
            )}
          </div>
        </div>
      )}

      {configErr && (
        <div style={{ fontSize: font.size.sm, color: colors.accent.yellow, padding: '8px 12px', backgroundColor: 'rgba(255,140,58,0.08)', borderRadius: 6 }}>
          Target spreads unavailable — {configErr}
        </div>
      )}

      {/* The board */}
      <Card padding={0}>
        <div style={{ display: 'grid', gridTemplateColumns: GRID, gap: 10, padding: '10px 14px', borderBottom: `1px solid ${colors.border.default}` }}>
          <Th>COUNTER</Th>
          <Th align="right">MARKET</Th>
          <Th align="right">OUR BID</Th>
          <Th align="right">OUR ASK</Th>
          <Th align="right">AGE</Th>
          <Th align="right">TARGET</Th>
          <Th align="right">SUGGESTED</Th>
          <Th align="right">EDGE</Th>
          <Th align="right">ADV SEL</Th>
          <Th align="right">FILL %</Th>
          <Th align="right">INVENTORY</Th>
          <Th align="right">SKEW / UTIL</Th>
          <Th align="right">P&amp;L</Th>
        </div>

        {rows.map(r => {
          const tgtBid = config?.spread_bps_bid?.[r.ticker];
          const tgtAsk = config?.spread_bps_ask?.[r.ticker];
          const invTarget = config?.inventory_target?.[r.ticker];
          const skew = r.inv?.skew;
          const util = r.inv?.utilization;
          const pnl = r.pos ? (r.pos.totalPnL ?? r.pos.unrealizedPnL + r.pos.realizedPnL) : null;
          const advSel = typeof r.sug?.features?.adverse_selection_prob === 'number'
            ? r.sug.features.adverse_selection_prob
            : null;
          const sugBid = r.sug?.bid.price;
          const sugAsk = r.sug?.ask.price;

          return (
            <div
              key={r.ticker}
              style={{
                display: 'grid', gridTemplateColumns: GRID, gap: 10, padding: '11px 14px',
                borderBottom: `1px solid ${colors.border.subtle}`, alignItems: 'center', fontSize: font.size.base,
              }}
            >
              {/* Counter + regime */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <span style={{ color: colors.text.primary, fontWeight: 700 }}>{r.ticker}</span>
                <span style={{ fontSize: font.size.xs, color: colors.text.muted }}>
                  {r.regime ? String(r.regime.regime).toUpperCase() : r.inst.secId}
                </span>
              </div>

              {/* The market's own top of book. Null on a one-sided book, which is the normal
                  state for most of our counters -- shown as a side-specific dash, not a zero. */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontSize: font.size.sm }}>
                {r.book ? (
                  <span title={r.book.spreadBps != null ? `Spread ${r.book.spreadBps.toFixed(0)} bps as of ${r.book.asOf}` : `One-sided book as of ${r.book.asOf}`}>
                    <span style={{ color: r.book.bestBid != null ? colors.accent.green : colors.text.muted }}>
                      {r.book.bestBid != null ? fmtNum(r.book.bestBid, 0) : DASH}
                    </span>
                    <span style={{ color: colors.text.muted }}> / </span>
                    <span style={{ color: r.book.bestAsk != null ? colors.accent.red : colors.text.muted }}>
                      {r.book.bestAsk != null ? fmtNum(r.book.bestAsk, 0) : DASH}
                    </span>
                    <div style={{ fontSize: font.size.xs, display: 'flex', gap: 5, justifyContent: 'flex-end', alignItems: 'baseline' }}>
                      {r.book.spreadBps != null && <span style={{ color: colors.text.muted }}>{r.book.spreadBps.toFixed(0)}bps</span>}
                    </div>
                  </span>
                ) : <Absent reason="No ORDER_BOOK message received for this counter yet." />}
              </div>

              {/* Our working bid / ask — the actual resting market */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {r.working?.bid
                  ? <span style={{ color: colors.accent.green, fontWeight: 600 }}>{fmtNum(r.working.bid.price)}</span>
                  : <Absent reason="No resting buy order for this counter in the orders this dashboard has received." />}
              </div>
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {r.working?.ask
                  ? <span style={{ color: colors.accent.red, fontWeight: 600 }}>{fmtNum(r.working.ask.price)}</span>
                  : <Absent reason="No resting sell order for this counter in the orders this dashboard has received." />}
              </div>

              {/* Quote age — derived from the ORDER_ACK timestamp we already store. The brain
                  carries no placed_at on its active-order map, so this is the client's view. */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: colors.text.secondary }}>
                {r.working?.bid || r.working?.ask
                  ? relAge((r.working.bid ?? r.working.ask)?.time)
                  : <Absent reason="No resting order to age. When present this is derived from the client's ORDER_ACK timestamp, not from the engine -- the brain's active-order map carries no placed_at." />}
              </div>

              {/* Configured target spread */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: colors.text.secondary }}>
                {tgtBid != null || tgtAsk != null
                  ? <span title="Configured in strategy_params (REST), not the engine's live in-memory value.">{tgtBid ?? DASH}/{tgtAsk ?? DASH}</span>
                  : <Absent reason="No per-symbol spread configured for this counter." />}
              </div>

              {/* The engine's intended quote */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {sugBid != null || sugAsk != null ? (
                  <span style={{ color: colors.text.secondary }} title={r.sug?.reason}>
                    {fmtNum(sugBid)}/{fmtNum(sugAsk)}
                  </span>
                ) : r.sug ? (
                  <span title={`Engine produced a suggestion with null prices. Reason: ${r.sug.reason}`} style={{ color: colors.accent.yellow, fontSize: font.size.xs, fontWeight: 700 }}>
                    NULL
                  </span>
                ) : (
                  <Absent reason="No suggestion received for this counter." />
                )}
              </div>

              {/* Edge / spread capture. The library computes this per ticker
                  (stats_collector capture_bps) but it is EOD-only and never published -- the
                  wire carries global scalars, and PERFORMANCE_UPDATE's only per-ticker block is
                  ticker_pnl. Blank until it is added to the per-ticker emit. */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                <Absent reason="Per-ticker spread capture is computed in the library but not published on the wire." />
              </div>

              {/* Adverse selection. This one IS available: the engine ships it on each quote
                  suggestion's feature vector. */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {advSel != null
                  ? <span style={{ color: advSel > 0.6 ? colors.accent.red : advSel > 0.35 ? colors.accent.yellow : colors.text.secondary }}>
                      {(advSel * 100).toFixed(0)}%
                    </span>
                  : <Absent reason="No adverse-selection probability on the latest suggestion for this counter." />}
              </div>

              {/* Fill ratio. Needs fills-vs-quotes per ticker, which the performance emit does
                  not carry either. */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                <Absent reason="Per-ticker fill ratio is not published on the wire." />
              </div>

              {/* Inventory vs target */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {r.inv ? (
                  <span style={{ color: r.inv.quantity >= 0 ? colors.text.secondary : colors.accent.red }}>
                    {fmtQty(r.inv.quantity)}
                    {invTarget != null && <span style={{ color: colors.text.muted, fontSize: font.size.xs }}> /{fmtQty(invTarget)}</span>}
                  </span>
                ) : <Absent reason="No INVENTORY_UPDATE received for this counter." />}
              </div>

              {/* Skew / utilization */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {skew != null && util != null ? (
                  <span>
                    <span style={{ color: Math.abs(skew) > 0.7 ? colors.accent.red : Math.abs(skew) > 0.35 ? colors.accent.yellow : colors.accent.green }}>
                      {skew >= 0 ? '+' : ''}{skew.toFixed(2)}
                    </span>
                    <span style={{ color: colors.text.muted }}> / </span>
                    <span style={{ color: util > 0.8 ? colors.accent.red : util > 0.5 ? colors.accent.yellow : colors.text.secondary }}>
                      {(util * 100).toFixed(0)}%
                    </span>
                  </span>
                ) : <Absent reason="No inventory state for this counter." />}
              </div>

              {/* P&L */}
              <div style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                {pnl != null
                  ? <span style={{ color: pnl > 0 ? colors.accent.green : pnl < 0 ? colors.accent.red : colors.text.muted, fontWeight: 600 }}>{fmtTZS(pnl)}</span>
                  : <Absent reason="No POSITION_UPDATE received for this counter." />}
              </div>
            </div>
          );
        })}
      </Card>

      {/* FOCUSED COUNTER — the single-name view. Clicking a board row focuses a counter and
          this answers, in one place, what a market maker asks about THAT name: my two-sided
          market against the market's, my inventory against its cap, and what the engine last
          decided and why. */}
      {focused && (
        <Panel
          title="Focused counter"
          badge={`${focused.ticker}${focused.regime ? ` · ${String(focused.regime.regime).toUpperCase()}` : ''}`}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
            <FocusBlock title="OUR MARKET">
              <FocusRow label="Bid" value={focused.working?.bid ? fmtNum(focused.working.bid.price) : DASH} color={colors.accent.green} />
              <FocusRow label="Ask" value={focused.working?.ask ? fmtNum(focused.working.ask.price) : DASH} color={colors.accent.red} />
              <FocusRow label="Age" value={focused.working?.bid || focused.working?.ask ? relAge((focused.working.bid ?? focused.working.ask)?.time) : DASH} />
            </FocusBlock>

            <FocusBlock title="THE MARKET">
              <FocusRow label="Best bid" value={focused.book?.bestBid != null ? fmtNum(focused.book.bestBid, 0) : DASH} color={colors.accent.green} />
              <FocusRow label="Best ask" value={focused.book?.bestAsk != null ? fmtNum(focused.book.bestAsk, 0) : DASH} color={colors.accent.red} />
              <FocusRow label="Spread" value={focused.book?.spreadBps != null ? `${focused.book.spreadBps.toFixed(0)} bps` : DASH} />
            </FocusBlock>

            <FocusBlock title="INVENTORY">
              <FocusRow label="Position" value={focused.inv ? fmtQty(focused.inv.quantity) : DASH} />
              <FocusRow label="Settled" value={focused.inv ? fmtQty(focused.inv.settled_quantity) : DASH} />
              <FocusRow
                label="Utilisation"
                value={focused.inv ? `${(focused.inv.utilization * 100).toFixed(0)}%` : DASH}
                color={focused.inv && focused.inv.utilization > 0.8 ? colors.accent.red : undefined}
              />
              <FocusRow
                label="Can buy / sell"
                value={focused.inv ? `${focused.inv.can_buy ? 'Y' : 'N'} / ${focused.inv.can_sell ? 'Y' : 'N'}` : DASH}
                color={focused.inv && !focused.inv.can_sell ? colors.accent.yellow : undefined}
              />
            </FocusBlock>

            <FocusBlock title="LAST DECISION">
              <FocusRow label="Suggested bid" value={focused.sug?.bid.price != null ? fmtNum(focused.sug.bid.price) : DASH} />
              <FocusRow label="Suggested ask" value={focused.sug?.ask.price != null ? fmtNum(focused.sug.ask.price) : DASH} />
              <FocusRow label="Confidence" value={focused.sug ? `${(focused.sug.confidence * 100).toFixed(0)}%` : DASH} />
            </FocusBlock>
          </div>

          {focused.sug?.reason && (
            <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${colors.border.subtle}`, fontSize: font.size.sm, color: colors.text.secondary }}>
              <span style={{ color: colors.text.muted }}>Engine reason: </span>{focused.sug.reason}
            </div>
          )}
        </Panel>
      )}

      {/* INTERNAL — hidden from the board. What it cannot yet show, and why:
          EDGE and FILL % need per-ticker fields added to the performance emit (the library
          computes capture_bps EOD-only and never publishes it). QUEUE RANK is impossible, not
          merely unimplemented: the brain's book manager collapses each snapshot into one
          synthetic order per price level, so the queue is destroyed before publication and our
          own ids are a disjoint namespace. PER-COUNTER QUOTING on/off needs a per-symbol field
          on ConfigState; can_buy/can_sell are the honest stand-ins and are on the board. */}
      {false && (
      <Panel title="Not on this board, and why">
        <div style={{ fontSize: font.size.sm, color: colors.text.secondary, lineHeight: 1.7 }}>
          <strong style={{ color: colors.text.primary }}>Queue rank.</strong> Not merely unimplemented —
          impossible. The brain's book manager replaces each snapshot with ONE synthetic order per price
          level (id <code>SNP_&lt;source&gt;_&lt;ms&gt;_&lt;price&gt;</code>), so the queue is collapsed before
          publication. Our own ids (<code>MM_BID_&lt;ms&gt;_&lt;hex&gt;</code>) live in a disjoint namespace.
          Nobody can compute rank from aggregated depth.<br />
          <strong style={{ color: colors.text.primary }}>Edge / spread capture and fill ratio.</strong> The library tracks
          these but they are not in the per-ticker performance emit. With zero fills there is also nothing to report.<br />
          <strong style={{ color: colors.text.primary }}>Queue rank.</strong> Omitted rather than zeroed — a rank of 0
          reads as front-of-queue, the opposite of the truth.<br />
          <strong style={{ color: colors.text.primary }}>Per-counter quoting on/off.</strong> Only a global
          <code> is_quoting_active</code> exists. The honest per-counter substitutes that do exist today are
          <code> can_buy</code>/<code>can_sell</code> on INVENTORY_UPDATE.
        </div>
      </Panel>
      )}
    </div>
  );
}
